import { useContainer } from '../../../hooks';
import { IOptionItem, Options, Table, TableCell, TableHeadCell, TableRow } from '../../Common';
import { GlobalApi } from '../../../util/Global';
import { UserWithPicture } from '../../LeaveRequestPartials';
import { MembersPoliciesProgress } from '../MembersPoliciesProgress';
import { MembersAccordion } from '../MembersAccordion';
import { Link } from 'react-router-dom';
import { OrganizationMembersWithBranchDataDto } from '../../../commonTypes/acs';
import { useTranslation } from '../../../hooks/useTranslation';
import { Translation } from '../../../commonTypes/translation.types';
import { useSelector } from 'react-redux';
import { selectAuth } from '../../../store/slices/auth.slice';
import { checkIfAdminOrOwner } from '../../../api/utils/roleUtils';

interface IMembersTable {
    members: OrganizationMembersWithBranchDataDto[];
    openEditModal: () => void;
    openDeleteModal: () => void;
    setSelectedMember: (member: OrganizationMembersWithBranchDataDto) => void;
    selectedOrganizationPolicyId?: string;
    isDataReady: boolean;
}

export const OrganizationMembers = ({
    members,
    openEditModal,
    openDeleteModal,
    setSelectedMember,
    selectedOrganizationPolicyId,
    isDataReady,
}: IMembersTable) => {
    const { t } = useTranslation();
    const globalApi: GlobalApi = useContainer(GlobalApi);

    const authUser = useSelector(selectAuth);

    const deleteUser = (member: OrganizationMembersWithBranchDataDto) => {
        openDeleteModal();
        setSelectedMember(member);
    };

    const editUser = (member: OrganizationMembersWithBranchDataDto) => {
        openEditModal();
        setSelectedMember(member);
    };

    const generateOptions = (member: OrganizationMembersWithBranchDataDto): IOptionItem[] => {
        const hasSelectedPolicy = member.policies.some(policy => policy.organizationPolicyId === selectedOrganizationPolicyId);
        const defaultOptions: IOptionItem[] = [
            {
                label: t('members.removeUser'),
                color: 'error',
                onClick: () => {
                    deleteUser(member);
                },
            },
        ];
        if (hasSelectedPolicy) {
            defaultOptions.unshift({
                label: t('members.editPolicy'),
                onClick: () => {
                    editUser(member);
                },
            });
        }
        return defaultOptions;
    };

    const isLastEl = (index: number) => {
        return index === members.length - 1;
    };

    const tableLeaveType = members.length && members[0].policies.length ? members[0].policies[0].leaveType : t('leaveRequest.availableDays');

    return (
        <>
            {globalApi.innerWidth() >= 768 ? (
                <Table className="organization-members">
                    <TableRow>
                        <TableHeadCell>{t('common.name')}</TableHeadCell>
                        <TableHeadCell>{t('common.role')}</TableHeadCell>
                        <TableHeadCell>{t('common.branch')}</TableHeadCell>
                        <TableHeadCell>{tableLeaveType}</TableHeadCell>
                    </TableRow>
                    {members.length > 0 ? (
                        members.map((member, i) => (
                            <TableRow key={member.userSub}>
                                <TableCell noBottomBorder={isLastEl(i)}>
                                    <Link to={`/profile/${member.userSub}`}>
                                        <UserWithPicture
                                            firstName={member.givenName}
                                            lastName={member.familyName}
                                            imgUrl={member.imageUrl}
                                            email={member.email}
                                        />
                                    </Link>
                                </TableCell>
                                <TableCell noBottomBorder={isLastEl(i)}>{t(`db_keywords.${member.role.name}` as keyof Translation)}</TableCell>
                                <TableCell noBottomBorder={isLastEl(i)}>{member.branchName}</TableCell>
                                <TableCell noBottomBorder={isLastEl(i)} className="organization-members__available-days">
                                    <div className="organization-members__policy">
                                        <MembersPoliciesProgress
                                            member={member}
                                            selectedOrganizationPolicyId={selectedOrganizationPolicyId ?? members[0].policies[0].organizationPolicyId}
                                            isDataReady={isDataReady}
                                        />
                                        {checkIfAdminOrOwner(authUser.role?.name) && (
                                            <Options className="organization-members__options" options={generateOptions(member)} />
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={4} className="organization-members__no-results">
                                {t('common.noResults')}
                            </TableCell>
                        </TableRow>
                    )}
                </Table>
            ) : (
                <div>
                    {members.map(member => (
                        <MembersAccordion
                            key={member.userSub}
                            member={member}
                            openDeleteModal={() => deleteUser(member)}
                            openEditModal={() => editUser(member)}
                            selectedOrganizationPolicyId={selectedOrganizationPolicyId ?? members[0].policies[0].organizationPolicyId}
                        />
                    ))}
                </div>
            )}
        </>
    );
};
